import React, { useState, useEffect, useRef } from 'react';

interface ServiceIntroSectionProps {
  onVisibilityChange?: (isVisible: boolean) => void;
}

const ServiceIntroSection: React.FC<ServiceIntroSectionProps> = ({ onVisibilityChange }) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [price, setPrice] = useState(6900);
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);
  const touchStartX = useRef<number>(0);
  const touchEndX = useRef<number>(0);
  const isDragging = useRef(false);

  const handleDragStart = (e: React.MouseEvent | React.TouchEvent) => {
    isDragging.current = true;
    if ('touches' in e) {
      touchStartX.current = e.touches[0].clientX;
    } else {
      touchStartX.current = e.clientX;
    }
  };

  const handleDragMove = (e: React.MouseEvent | React.TouchEvent) => {
    if (!isDragging.current) return;
    
    if ('touches' in e) {
      touchEndX.current = e.touches[0].clientX;
    } else {
      touchEndX.current = e.clientX;
    }
  };

  const handleDragEnd = () => {
    if (!isDragging.current) return;
    
    const diff = touchStartX.current - touchEndX.current;
    const threshold = 50; // 스와이프 감지 임계값

    if (Math.abs(diff) > threshold) {
      if (diff > 0) {
        // 왼쪽으로 스와이프
        handleNext();
      } else {
        // 오른쪽으로 스와이프
        handlePrev();
      }
    }
    
    isDragging.current = false;
  };

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !isVisible) {
          setIsVisible(true);
          onVisibilityChange?.(true);
          
          // 5초 후에 애니메이션 시작
          setTimeout(() => {
            let startPrice = 6900;
            const endPrice = 990;
            const duration = 2000; // 2초
            const steps = 60; // 60프레임
            const stepDuration = duration / steps;
            const priceStep = (startPrice - endPrice) / steps;

            const timer = setInterval(() => {
            startPrice -= priceStep;
            if (startPrice <= endPrice) {
              setPrice(endPrice);
              clearInterval(timer);
            } else {
              setPrice(Math.round(startPrice));
            }
          }, stepDuration);
          }, 3000); // 5초 딜레이
        }
      },
      {
        threshold: 0.2 // 20% 이상 보일 때 트리거
      }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, [isVisible]);

  const images = [
    '/images/page01.png',
    '/images/page02.png',
    '/images/page03.png',
    '/images/page04.png'
  ];

  const handleNext = () => {
    setCurrentSlide((prev) => (prev + 1) % images.length);
  };

  const handlePrev = () => {
    setCurrentSlide((prev) => (prev - 1 + images.length) % images.length);
  };

  return (
    <section ref={sectionRef} className="pt-0 pb-20 px-4 bg-gradient-to-b from-white via-orange-50/20 to-white relative overflow-hidden">
      {/* 장식용 배경 요소들 */}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
        <div className="absolute -top-24 -left-24 w-48 h-48 bg-orange-200 rounded-full opacity-20 blur-3xl"></div>
        <div className="absolute -bottom-12 -right-12 w-32 h-32 bg-orange-300 rounded-full opacity-20 blur-2xl"></div>
        <div className="absolute top-1/2 -right-16 w-24 h-24 bg-orange-100 rounded-full opacity-30 blur-xl"></div>
      </div>

      <div className="max-w-[480px] mx-auto relative">
          {/* 제목 섹션 */}
          <div className="mb-10">
            <h2 className="text-3xl font-extrabold leading-tight text-left space-y-2">
              <div className="flex items-center gap-2">
                <span className="text-gray-900">일주일</span>
                <div className="bg-orange-50 rounded-xl px-3 py-1 border border-orange-200 transition-all duration-300">
                  <span className="text-orange-500">{price.toLocaleString()}</span>
                  <span className="text-orange-500">원</span>
                </div>
                <span className="text-gray-900">에</span>
              </div>
              <div className="text-orange-500">매일 마감할인빵 1개무료!</div>
            </h2>
          </div>

        <div className="relative">
          {/* Carousel Container */}
          <div className="overflow-hidden rounded-3xl">
            <div 
              className="flex transition-transform duration-500 ease-out"
              style={{ transform: `translateX(-${currentSlide * 100}%)` }}
              onTouchStart={handleDragStart}
              onTouchMove={handleDragMove}
              onTouchEnd={handleDragEnd}
              onMouseDown={handleDragStart}
              onMouseMove={handleDragMove}
              onMouseUp={handleDragEnd}
              onMouseLeave={handleDragEnd}
            >
              {images.map((image, index) => (
                <div 
                  key={index}
                  className="w-full flex-shrink-0"
                >
                  <img 
                    src={image} 
                    alt={`떠리 패스 소개 ${index + 1}`}
                    className="w-full h-auto"
                  />
                </div>
              ))}
            </div>
          </div>

          {/* Navigation Button */}
          <button 
            onClick={handleNext}
            className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/10 shadow-lg rounded-full p-3 hover:bg-white/90 transition-all transform hover:scale-110 active:scale-95"
            aria-label="다음 슬라이드"
          >
            <svg className="w-6 h-6 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>

        {/* Dots Indicator */}
        <div className="flex justify-center gap-3 mt-8">
          {images.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentSlide(index)}
              className={`h-2.5 rounded-full transition-all transform hover:scale-110 ${
                currentSlide === index 
                  ? 'bg-orange-500 w-10 shadow-md' 
                  : 'bg-orange-200 hover:bg-orange-300 w-2.5'
              }`}
              aria-label={`슬라이드 ${index + 1}로 이동`}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default ServiceIntroSection;
